import { useMutation } from '@tanstack/react-query';
import { notification } from 'antd';
import { useTranslation } from 'react-i18next';
import { FinancialDocumentAllocation } from '../../../../../../models/IncomingInvoices';
import { apiGetAllocationsForFinancialDocument } from '../../../../api';
import { useIncomingInvoiceDrawerContext } from '../incomingInvoiceComponents/IncomingInvoiceContextProvider';
import { useCreateAllocationsMutation } from './useCreateAllocationMutation';
import { useUpdateAllocationsMutation } from './useUpdateAllocationMutation';

export function useSaveAllocationsMutation(onSaveComplete?: () => void) {
  const { t } = useTranslation();
  const createMutation = useCreateAllocationsMutation();
  const updateMutation = useUpdateAllocationsMutation();

  const {
    invoiceAllocations,
    setInvoiceAllocations,
    initialAllocations,
    setInitialAllocations,
    financialDocumentId,
  } = useIncomingInvoiceDrawerContext();

  const extractRelevantFields = (
    allocation: Partial<FinancialDocumentAllocation>
  ) => ({
    financialDocumentId: allocation.financialDocumentId,
    projectId: allocation.projectId,
    projectPhaseId: allocation.projectPhaseId,
    amount: allocation.amount,
  });

  return useMutation({
    mutationFn: async () => {
      if (!invoiceAllocations || invoiceAllocations.length === 0) {
        return { hasChanges: false };
      }

      const allocationsByProject: Record<
        string,
        Partial<FinancialDocumentAllocation>[]
      > = {};
      invoiceAllocations.forEach((alloc) => {
        if (!alloc.projectId) return;
        if (!allocationsByProject[alloc.projectId]) {
          allocationsByProject[alloc.projectId] = [];
        }
        allocationsByProject[alloc.projectId].push(alloc);
      });

      let hasChanges = false;

      for (const projectId of Object.keys(allocationsByProject)) {
        const allocations = allocationsByProject[projectId];

        const newAllocations = allocations.filter(
          (a) => !a.financialDocumentAllocationId
        );

        const changedAllocations = allocations.filter((localAlloc) => {
          if (!localAlloc.financialDocumentAllocationId) return false;

          const originalAlloc = (initialAllocations || []).find(
            (orig) =>
              orig.financialDocumentAllocationId ===
              localAlloc.financialDocumentAllocationId
          );

          if (!originalAlloc) return false;

          const localRelevant = extractRelevantFields(localAlloc);
          const originalRelevant = extractRelevantFields(originalAlloc);

          return (
            JSON.stringify(localRelevant) !== JSON.stringify(originalRelevant)
          );
        });

        if (newAllocations.length > 0) {
          await createMutation.mutateAsync({
            projectId,
            financialDocumentId,
            allocations: newAllocations as FinancialDocumentAllocation[],
          });
          hasChanges = true;
        }

        if (changedAllocations.length > 0) {
          await updateMutation.mutateAsync({
            projectId,
            financialDocumentId,
            allocations: changedAllocations as FinancialDocumentAllocation[],
          });
          hasChanges = true;
        }
      }

      return { hasChanges };
    },
    onSuccess: async (result) => {
      if (
        !result.hasChanges ||
        !invoiceAllocations ||
        invoiceAllocations.length === 0
      ) {
        onSaveComplete?.();
        return;
      }

      const projectIdForRefetch = invoiceAllocations.find(
        (a) => !!a.projectId
      )?.projectId;

      if (!projectIdForRefetch) {
        onSaveComplete?.();
        return;
      }

      const freshAllocations = await apiGetAllocationsForFinancialDocument(
        projectIdForRefetch,
        financialDocumentId
      );

      const freshData = freshAllocations.data ?? [];
      setInvoiceAllocations(freshData);
      setInitialAllocations(freshData);

      onSaveComplete?.();

      if (result.hasChanges) {
        notification.success({
          message: t(
            'accounting:incomingInvoices.drawerSteps.allocations.notifications.allocationSavedTitle'
          ),
          description: t(
            'accounting:incomingInvoices.drawerSteps.allocations.notifications.allocationSavedMessage'
          ),
        });
      }
    },
    onError: (error) => {
      notification.error({
        message: t(
          'accounting:incomingInvoices.drawerSteps.allocations.notifications.allocationSaveErrorTitle'
        ),
        description:
          error instanceof Error
            ? error.message
            : t(
                'accounting:incomingInvoices.drawerSteps.allocations.notifications.allocationSaveErrorMessage'
              ),
      });
    },
  });
}
